import React, { useState } from 'react';
import { Form } from 'antd';
import type { FormProps } from 'antd';
import AppModalContainer from '@nxsys/components/AppModalContainer';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppSelect from '@nxsys/components/AppSelect';
import AppDatePicker from '@nxsys/components/AppDatePicker';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { DocumentPayload } from '@nxsys/types/models/document';
import { defaultDocumentTypeOptionsGroup } from '@nxsys/constants/AppConstants';
import { getDocumentTypeOptions } from '@nxsys/utils/rightToWorkOptions';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { useSelector } from 'react-redux';

type CreateModalProps = {
  isOpen: boolean;
  title: string;
  toggle: () => void;
  onCreatedDocument: (params: DocumentPayload) => Promise<void>;
};

const CreateDocumentModal: React.FC<CreateModalProps> = ({
  title,
  isOpen,
  toggle,
  onCreatedDocument,
}: CreateModalProps) => {
  const [form] = Form.useForm();
  const { messages } = useTypedIntl();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );

  const onCancel = () => {
    console.log('Cancel');
    form.resetFields();
    toggle();
  };
  const onFinish: FormProps['onFinish'] = async (values) => {
    try {
      setIsSubmitting(true);
      const params = {
        ...values,
        documentType: values?.documentType?.value || null,
        expiryDate: values?.expiryDate?.toDate() || null,
        documentDate: values?.documentDate?.toDate() || null,
      };
      await onCreatedDocument(params);
      onCancel();
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const onFinishFailed: FormProps['onFinishFailed'] = (errorInfo) => {
    console.log('Failed:', errorInfo);
  };

  return (
    <AppModalContainer
      title={title}
      isOpen={isOpen}
      toggle={toggle}
      actions={[
        {
          name: messages['common.cancel'],
          variant: 'outlined',
          buttonType: 'primary',
          onClick: onCancel,
        },
        {
          name: messages['common.save'],
          buttonType: 'primary',
          onClick: () => {
            console.log('Create');
            form.submit();
          },
          loading: isSubmitting,
          disabled: isSubmitting,
        },
      ]}
    >
      <AppFormContainer
        form={form}
        onFinish={onFinish}
        onFinishFailed={onFinishFailed}
      >
        <Form.Item
          label={messages['common.documentTitle']}
          name="documentTitle"
          rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
        >
          <AppInput placeholder={messages['form.enter']} />
        </Form.Item>
        <Form.Item
          label={messages['common.documentType']}
          name="documentType"
          rules={[{ required: true, message: messages['form.pleaseEnter'] }]}
        >
          <AppSelect
            options={getDocumentTypeOptions(
              isBureau
                ? defaultDocumentTypeOptionsGroup?.filter(
                    (item) => item.value !== 'AGENCY_QUESTIONNAIRE',
                  )
                : defaultDocumentTypeOptionsGroup,
              payrollCountry,
            )}
            placeholder={messages['form.select']}
          />
        </Form.Item>
        <Form.Item label={messages['common.expiryDate']} name="expiryDate">
          <AppDatePicker />
        </Form.Item>
        <Form.Item label={messages['common.documentDate']} name="documentDate">
          <AppDatePicker />
        </Form.Item>
        <Form.Item
          label={messages['common.attachment']}
          name="documentFile"
          rules={[{ required: true, message: messages['form.pleaseSelect'] }]}
        >
          <AppUploadFile
            onChange={(file: File) => {
              form.setFieldsValue({ documentFile: file });
            }}
          />
        </Form.Item>
      </AppFormContainer>
    </AppModalContainer>
  );
};

export default CreateDocumentModal;
